import React, { useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Table } from "../../common/Table/Table";
import { StatusBadge } from "../../common/Table/StatusBadge/StatusBadge";
import { useLogs } from "../../../context/LogsContext";
import { useData } from "../../../context/DataContext";
import { SearchForm } from "./SearchForm/SearchForm";
import { Pagination } from "./Pagination/Pagination";
import { useAnswerLogsSearch } from "../../../hooks/useAnswerLogsSearch";
import { useAnswerLogsPagination } from "../../../hooks/useAnswerLogsPagination";
import { mapAnswerData } from "../../../helpers/answer-logs-helpers";
import { Container, ResultsCount, ErrorMessage } from "./AnswerLog.styles";

export const AnswerLogs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Context data
  const {
    answers,
    isLoadingAnswers,
    error,
    fetchAnswersForPage,
    fetchedPages,
    generateCacheKey,
    queryCache,
  } = useLogs();

  const { organizations, fetchOrganizations } = useData();

  // Custom hooks for search and pagination
  const {
    searchMode,
    answerIdQuery,
    emailQuery,
    selectedOrgId,
    isSearching,
    currentSearchPageRef,
    effectivePagination,
    handleSearchModeChange,
    handleAnswerIdChange,
    handleEmailChange,
    handleOrgSelect,
    getCurrentSearchQuery,
  } = useAnswerLogsSearch();

  const { pageButtons, showPagination } = useAnswerLogsPagination({
    effectivePagination,
    isSearching,
    currentSearchPageRef,
    getCurrentSearchQuery,
    searchMode,
  });

  // Fetch organizations when component mounts
  useEffect(() => {
    if (organizations.length === 0) {
      fetchOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check cache first when loading initial page or changing pages
  useEffect(() => {
    if (!isSearching) {
      const cacheKey = generateCacheKey(
        effectivePagination.page,
        effectivePagination.limit
      );
      const isCached = !!queryCache[cacheKey];

      if (!isCached && !fetchedPages.includes(effectivePagination.page)) {
        fetchAnswersForPage(effectivePagination.page);
      }
    }
  }, [
    effectivePagination.page,
    effectivePagination.limit,
    fetchedPages,
    fetchAnswersForPage,
    isSearching,
    generateCacheKey,
    queryCache,
  ]);

  // Setup columns - memoized to prevent recreation on each render
  const columns = useMemo(
    () => [
      { key: "id", header: t("logs.answerId"), align: "center" },
      { key: "chatId", header: t("logs.chatId"), align: "center" },
      { key: "userId", header: t("logs.userId"), align: "center" },
      { key: "userName", header: t("logs.userName"), align: "center" },
      { key: "orgName", header: t("logs.orgName"), align: "center" },
      { key: "status", header: t("logs.status"), align: "center" },
      { key: "tokens", header: t("logs.tokens"), align: "center" },
      { key: "cost", header: t("logs.cost"), align: "center" },
    ],
    [t]
  );

  // Map data for display
  const currentPageData = useMemo(() => {
    return answers.map(mapAnswerData);
  }, [answers]);

  // Handle row click
  const handleRowClick = useCallback(
    (item) => {
      navigate(`/logs/answers/${item.id}`);
    },
    [navigate]
  );

  // Render custom cell
  const renderCustomCell = useCallback(
    (item, key) => {
      if (key === "status") {
        return (
          <StatusBadge status={item.status} text={t(`status.${item.status}`)} />
        );
      }
      return item[key];
    },
    [t]
  );

  // Memoize results count
  const resultsCount = useMemo(() => {
    const currentQuery = getCurrentSearchQuery();
    if (currentQuery) {
      return (
        <ResultsCount>
          {t("logs.resultsCount", {
            count: isSearching ? effectivePagination.total : 0,
          })}
        </ResultsCount>
      );
    }
    return null;
  }, [getCurrentSearchQuery, t, isSearching, effectivePagination.total]);

  return (
    <Container>
      <SearchForm
        searchMode={searchMode}
        answerIdQuery={answerIdQuery}
        emailQuery={emailQuery}
        selectedOrgId={selectedOrgId}
        handleSearchModeChange={handleSearchModeChange}
        handleAnswerIdChange={handleAnswerIdChange}
        handleEmailChange={handleEmailChange}
        handleOrgSelect={handleOrgSelect}
        organizations={organizations}
      />

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {resultsCount}

      <Table
        columns={columns}
        data={currentPageData}
        isLoading={isLoadingAnswers}
        emptyMessage={t("logs.noResults")}
        loadingMessage={t("logs.loading")}
        renderCustomCell={renderCustomCell}
        onRowClick={handleRowClick}
      />

      {showPagination && (
        <Pagination
          currentPage={effectivePagination.page}
          total={effectivePagination.total}
          limit={effectivePagination.limit}
          pageButtons={pageButtons}
        />
      )}
    </Container>
  );
};
